// Pure helpers for rendering a "cluster" of work items as a single review unit.
//
// A cluster = the set of work items sharing one `clusterKey` (already grouped by the
// dashboard). When a cluster introduces a new micro-lesson NODE, that `create` item is
// the ANCHOR; its associated prerequisite rewires (edge-only `fix` items) and any other
// changes hang off it. The UI shows the anchor first, a human breakdown ("1 new node ·
// 3 prerequisite rewires"), an aggregate status, and a single approve/cancel action —
// while each member stays individually editable/approvable. No data model changes: the
// anchor is DERIVED from ops, not stored, so nothing here pollutes the applied patch.

import type { WorkItemSummary } from '../types/workflow.js';
import { itemLowestPage } from './sources.js';

// Statuses a reviewer can still act on (approve / cancel) before execution.
const CANCELLABLE: ReadonlySet<string> = new Set(['pending', 'approved', 'approved_with_edit']);

export interface ClusterStatusSummary {
  total: number;
  pending: number;
  reviewed: number; // approved + approved_with_edit + executed
  cancelled: number;
}

export interface ClusterMeta {
  clusterKey: string;
  title: string; // anchor title, else a humanized clusterKey
  anchor: WorkItemSummary | null; // the new-node create that anchors the cluster (if any)
  isNewNodeCluster: boolean;
  ordered: WorkItemSummary[]; // ascending cited page, anchor first among ties, then createdAt
  newNodeCount: number; // create + entity
  newModuleCount: number; // create + group
  newPracticeItemCount: number; // create + task (decoupled practice item, ELPI lane)
  rewireCount: number; // edge-only prerequisite fix
  breakdown: string; // e.g. "1 new node · 3 prerequisite rewires · 1 rename"
  pending: WorkItemSummary[]; // members that can be approved
  cancellable: WorkItemSummary[]; // members that can be cancelled
  restorable: WorkItemSummary[]; // cancelled members that can be returned to the queue
  status: ClusterStatusSummary;
}

/**
 * Stable ordering key for a cluster that does NOT depend on member status (so a cluster never jumps
 * when a member is approved/rejected — R-1): ascending lowest cited page (R-5), then earliest
 * createdAt, then clusterKey.
 *
 * The page comparison must not subtract: a cluster with no cited page scores POSITIVE_INFINITY, and
 * `Infinity - Infinity` is `NaN`, which `Array#sort` reads as "equal" — so for a cohort with no
 * source refs at all (every ELPI cluster today) the documented createdAt/key tie-breakers never ran
 * and the order was whatever the API happened to return. Compare, then fall through.
 */
export function compareClusters(
  left: { clusterKey: string; items: WorkItemSummary[] },
  right: { clusterKey: string; items: WorkItemSummary[] },
): number {
  const leftPage = clusterLowestPage(left.items);
  const rightPage = clusterLowestPage(right.items);
  if (leftPage !== rightPage) {
    return leftPage < rightPage ? -1 : 1;
  }
  const createdDelta = clusterEarliestCreatedAt(left.items).localeCompare(clusterEarliestCreatedAt(right.items));
  if (createdDelta !== 0) {
    return createdDelta;
  }
  return left.clusterKey.localeCompare(right.clusterKey);
}

export function clusterLowestPage(items: WorkItemSummary[]): number {
  return items.reduce(
    (lowest, item) => Math.min(lowest, itemLowestPage(item) ?? Number.POSITIVE_INFINITY),
    Number.POSITIVE_INFINITY,
  );
}

export function clusterEarliestCreatedAt(items: WorkItemSummary[]): string {
  return items.reduce(
    (earliest, item) => (item.createdAt < earliest ? item.createdAt : earliest),
    items[0]?.createdAt ?? '',
  );
}

/** True when a `fix` patch only changes prerequisite edges (no internal content). */
export function isPrereqRewirePatch(patch: unknown): boolean {
  return Boolean(patch && typeof patch === 'object' && 'prerequisites' in (patch as Record<string, unknown>));
}

/** "quants:new-node:benchmarking" -> "Quants · New Node · Benchmarking" (fallback title only). */
export function humanizeClusterKey(clusterKey: string): string {
  return clusterKey
    .split(':')
    .map((segment) =>
      segment
        .split(/[-_]/g)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .filter(Boolean)
    .join(' · ');
}

/**
 * Display title carried by the item's own patch, or null when the patch has none (e.g. a
 * prerequisite rewire — its patch is only the edge change, by design). Callers that can
 * (like the queue row) resolve the target entity's live title for the null case.
 */
export function workItemPatchTitle(item: WorkItemSummary): string | null {
  const titlePatch = item.suggestedPatch.title;
  if (typeof titlePatch === 'string') {
    return titlePatch;
  }
  if (titlePatch && typeof titlePatch === 'object') {
    const after = (titlePatch as Record<string, unknown>).after;
    const before = (titlePatch as Record<string, unknown>).before;
    if (typeof after === 'string' && after.trim()) {
      return after;
    }
    if (typeof before === 'string' && before.trim()) {
      return before;
    }
  }
  if (typeof item.suggestedPatch.name === 'string') {
    return item.suggestedPatch.name;
  }
  return null;
}

/** Best-effort display title for a single work item (mirrors the queue row title). */
export function workItemTitle(item: WorkItemSummary): string {
  return workItemPatchTitle(item) ?? item.targetId ?? item.workItemId;
}

function isNewNode(item: WorkItemSummary): boolean {
  return item.op === 'create' && item.targetType === 'entity';
}

function isNewModule(item: WorkItemSummary): boolean {
  return item.op === 'create' && item.targetType === 'group';
}

/** A decoupled practice-item create (ELPI lane) — a new task, NOT a concept-map node. */
function isNewPracticeItem(item: WorkItemSummary): boolean {
  return item.op === 'create' && item.targetType === 'task';
}

function isRewire(item: WorkItemSummary): boolean {
  return item.op === 'fix' && isPrereqRewirePatch(item.suggestedPatch);
}

function plural(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

/**
 * Derive presentation metadata for one cluster. `items` is expected pre-sorted by
 * createdAt (as the dashboard groups them); the anchor is pulled to the front.
 */
export function deriveClusterMeta(clusterKey: string, items: WorkItemSummary[]): ClusterMeta {
  // Prefer a new MODULE (group create) as the anchor — it's the structural parent of the
  // cluster's new nodes — then a node (entity) create, so a decoupled practice item never
  // titles a mixed lesson + ELPI cluster. Batch items often share a createdAt, so never
  // rely on ordering to surface the anchor.
  const anchor =
    items.find((item) => item.op === 'create' && item.targetType === 'group') ??
    items.find((item) => item.op === 'create' && item.targetType === 'entity') ??
    items.find((item) => item.op === 'create') ??
    null;
  // Order by ascending cited page so the reviewer moves forward through the curriculum instead of
  // jumping back and forth (AD feedback R-5). The anchor (new module/node) leads when pages tie or
  // are absent; createdAt is the final tiebreaker so batches with a shared createdAt stay stable.
  const ordered = [...items].sort((left, right) => {
    const leftPage = itemLowestPage(left) ?? Number.POSITIVE_INFINITY;
    const rightPage = itemLowestPage(right) ?? Number.POSITIVE_INFINITY;
    if (leftPage !== rightPage) {
      return leftPage - rightPage;
    }
    if (left === anchor) {
      return -1;
    }
    if (right === anchor) {
      return 1;
    }
    return left.createdAt.localeCompare(right.createdAt);
  });

  const newNodeCount = items.filter(isNewNode).length;
  const newModuleCount = items.filter(isNewModule).length;
  const newPracticeItemCount = items.filter(isNewPracticeItem).length;
  const rewireCount = items.filter(isRewire).length;

  // Anything not counted above, grouped by op for the breakdown line.
  const otherByOp = new Map<string, number>();
  for (const item of items) {
    if (isNewNode(item) || isNewModule(item) || isNewPracticeItem(item) || isRewire(item)) {
      continue;
    }
    otherByOp.set(item.op, (otherByOp.get(item.op) ?? 0) + 1);
  }

  const parts: string[] = [];
  if (newModuleCount > 0) {
    parts.push(plural(newModuleCount, 'new module'));
  }
  if (newNodeCount > 0) {
    parts.push(plural(newNodeCount, 'new node'));
  }
  if (newPracticeItemCount > 0) {
    parts.push(plural(newPracticeItemCount, 'new practice item'));
  }
  if (rewireCount > 0) {
    parts.push(plural(rewireCount, 'prerequisite rewire'));
  }
  for (const [op, count] of otherByOp) {
    parts.push(plural(count, op));
  }

  const status: ClusterStatusSummary = {
    total: items.length,
    pending: items.filter((item) => item.status === 'pending').length,
    reviewed: items.filter((item) =>
      item.status === 'approved' || item.status === 'approved_with_edit' || item.status === 'executed',
    ).length,
    cancelled: items.filter((item) => item.status === 'cancelled').length,
  };

  return {
    clusterKey,
    title: anchor ? workItemTitle(anchor) : humanizeClusterKey(clusterKey),
    anchor,
    isNewNodeCluster: Boolean(anchor),
    ordered,
    newNodeCount,
    newModuleCount,
    newPracticeItemCount,
    rewireCount,
    breakdown: parts.join(' · ') || plural(items.length, 'item'),
    pending: items.filter((item) => item.status === 'pending'),
    cancellable: items.filter((item) => CANCELLABLE.has(item.status)),
    restorable: items.filter((item) => item.status === 'cancelled'),
    status,
  };
}
